import React, { useEffect, useState, useRef } from 'react'
import type { ChangeEvent, HTMLAttributes } from 'react'
import { Text, Checkbox, csx, Stack, cx, Grid } from '@vtex/admin-ui'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocalizationInput } from '@vtexlab/assisted-sales-localization'

import {
  Step,
  StepTitle,
  StepSubmitButton,
  StepInputText,
  StepOrderSummary,
} from '../../../../components'
import { formatAddressToExtended } from '../../../../utils'
import { useStep } from '../../../../hooks'
import { useCheckout } from '../../../../state'
import type { ClientDataAddress } from '../../../../typings'
import { PostalCodeText } from '../PostalCodeText'

export type AddressNumberStepProps = {
  address: ClientDataAddress
  onStepSubmit?: (addressNumber: string) => void
  onDataChange?: () => void
  className?: string
} & HTMLAttributes<HTMLFormElement>

export const AddressNumberStep = ({
  address,
  onStepSubmit,
  className = '',
  onDataChange = () => {},
  ...props
}: AddressNumberStepProps) => {
  const checkout = useCheckout()
  const inputAddressNumberRef = useRef<HTMLInputElement>(null)
  const { formatMessage } = useIntl()

  const [withoutNumber, setWithoutNumber] = useState(
    checkout.checkout.clientData?.address?.addressWithoutNumber
  )

  const numberInput = useLocalizationInput({
    inputField: 'number',
    initialValue: address.number,
    onChange: () => {
      if (numberInput.validate()) {
        setWithoutNumber(false)
      }

      onDataChange()
    },
  })

  const isNumberValid = numberInput.validate()

  const { formRef, handleSubmit, fireFormSubmit } = useStep({
    onSubmit() {
      if (onStepSubmit && isNumberValid) {
        onStepSubmit(numberInput.unformat())
      } else if (onStepSubmit && withoutNumber) {
        onStepSubmit('S/N')
      }
    },
  })

  const handleCheckWithoutNumberChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setWithoutNumber(event.target.checked)

    checkout.setClientDataAddressWithoutNumber(event.target.checked)
  }

  useEffect(() => {
    inputAddressNumberRef.current?.focus()
  }, [])

  return (
    <Step
      ref={formRef}
      onSubmit={handleSubmit}
      className={cx(
        csx({
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          '@desktop': {
            justifyContent: 'normal',
          },
        }),
        className
      )}
      {...props}
    >
      <Stack space="$space-10">
        <StepTitle>
          <FormattedMessage id="checkout.home-delivery.step.address-number.title" />
        </StepTitle>
      </Stack>
      <div
        className={csx({
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          height: '90%',
          alignItems: 'center',
        })}
      >
        <div className={csx({ flex: 1 })}>
          <Grid className={csx({ marginBottom: '$space-3', rowGap: '0.5rem' })}>
            <PostalCodeText value={address.postalCode} />
            <Text variant="pageTitle" tone="secondary">
              {formatAddressToExtended({
                ...address,
                number: numberInput.value,
              })}
            </Text>
            <StepInputText
              {...numberInput.inputAttrs}
              aria-label={formatMessage({
                id: 'checkout.home-delivery.step.address-number.label',
              })}
              disabled={withoutNumber}
              ref={inputAddressNumberRef}
              placeholder={formatMessage({
                id: 'checkout.home-delivery.step.address-number.placeholder',
              })}
              className={csx({ marginY: '$space-3' })}
            />

            <Checkbox
              className={csx({ marginTop: '$space-2' })}
              disabled={isNumberValid}
              checked={withoutNumber}
              label={
                <FormattedMessage id="checkout.home-delivery.step.address-number.without-number" />
              }
              onChange={handleCheckWithoutNumberChange}
            />
          </Grid>
        </div>
      </div>

      <StepOrderSummary>
        <StepSubmitButton
          type="submit"
          disabled={!isNumberValid && !withoutNumber}
          onClick={fireFormSubmit}
        >
          <FormattedMessage id="continue" />
        </StepSubmitButton>
      </StepOrderSummary>
    </Step>
  )
}
